import AccordionContent from '../../../common/AccordionContent';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { translate } from 'components/Translation';
import ActivateGobizDialog from './subcomponents/ActivateGobizDialog';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import { finalize } from 'rxjs/operators';
import { CustomAttribute, extractAttributeValue, extractAttributeValuesAll } from 'utils/ecommerce/services/extract';
import RoamingInternationalActivationDialog from '../RoamingInternationalActivationDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { Package } from 'utils/api/models/numbers/usage';
import { goBizPackageDescription } from 'views/Numbers/Details/Services/subcomponents/MobilePostpaid/subcomponents/JawazakContentWrapper/helpers';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { getProductId } from 'views/Numbers/Details/Services/helpers/getProductId';
import { getLanguage } from 'utils/language/language';

interface IGobizAccordionContentWrapperProps {
	termsAndConditions: types.services.termsAndConditions;
	updateOrderItems: types.services.updateOrderItemsFunction;
	subscriberProducts: SingleSubscriberProduct[];
	roamingEnabled: boolean;
	packages: Package[];
	defaultRoamingPayG: { id: string; place: string };
	roamingPending: boolean;
	setPendingStatus: (status: boolean) => void;
	setIsGoBizActive: React.Dispatch<React.SetStateAction<boolean>>;
}

type dialog = 'planActivationDialog' | 'topUpDialog' | 'changePlanDialog';

const GobizAccordionContentWrapper = ({
	termsAndConditions,
	updateOrderItems,
	roamingEnabled,
	packages,
	defaultRoamingPayG,
	roamingPending,
	subscriberProducts,
	setPendingStatus,
	setIsGoBizActive,
}: IGobizAccordionContentWrapperProps) => {
	const [planCatalogEntryView, setPlanCatalogEntryView] = useState<types.ecommerce.CatalogEntryView[]>([]);
	const [topUpCatalogEntryView, setTopUpCatalogEntryView] = useState<types.ecommerce.CatalogEntryView[]>([]);
	const [loadingPlans, setLoadingPlans] = useState<boolean>(true);
	const [loadingTopUps, setLoadingTopUps] = useState<boolean>(true);
	const [dialogType, setDialogType] = useState<dialog | null>(null);
	const [roamingDialogOpen, setRoamingDialogOpen] = useState<boolean>(false);

	const language = getLanguage();

	const getCatalogEntryView = (response: any): types.ecommerce.CatalogEntryView[] => {
		const _catalogEntryView = response.catalogEntryView;
		if (_catalogEntryView && Array.isArray(_catalogEntryView) && _catalogEntryView.length > 0) {
			return _catalogEntryView;
		}
		return [];
	};

	const pendingSubscriberProductIds = useMemo(
		() => subscriberProducts.filter(({ status }) => status !== 'Active').map(({ productId }) => productId),
		[subscriberProducts]
	);

	const pendingTopUpPackages: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return topUpCatalogEntryView.filter(({ partNumber }) => {
			return pendingSubscriberProductIds?.indexOf(getProductId(partNumber)) >= 0;
		});
	}, [topUpCatalogEntryView, pendingSubscriberProductIds]);

	const pendingPlanPackages: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return planCatalogEntryView.filter(({ partNumber }) => {
			return pendingSubscriberProductIds?.indexOf(getProductId(partNumber)) >= 0;
		});
	}, [planCatalogEntryView, pendingSubscriberProductIds]);

	useEffect(() => {
		setLoadingPlans(true);
		ECommerceService.getGroupContent(idConfig.SERVICES_TABLES.GOBIZ_PLAN[env])
			.pipe(finalize(() => setLoadingPlans(false)))
			.subscribe(({ response }: any) => {
				setPlanCatalogEntryView(getCatalogEntryView(response));
			});
	}, []);

	useEffect(() => {
		setLoadingTopUps(true);
		ECommerceService.getGroupContent(idConfig.SERVICES_TABLES.GOBIZ_TOP_UP[env])
			.pipe(finalize(() => setLoadingTopUps(false)))
			.subscribe(({ response }: any) => {
				setTopUpCatalogEntryView(getCatalogEntryView(response));
			});
	}, []);

	const showPlanActivationDialog = () => {
		setDialogType('planActivationDialog');
	};

	const showTopUpDialog = () => {
		setDialogType('topUpDialog');
	};

	const showChangePlanDialog = () => {
		setDialogType('changePlanDialog');
	};

	const hideDialog = () => {
		setDialogType(null);
	};

	const showRoamingDialog = () => {
		setRoamingDialogOpen(true);
	};

	const hideRoamingDialog = () => {
		setRoamingDialogOpen(false);
	};

	const validateReqProd = useCallback(
		(singleCatalogEntryView: types.ecommerce.CatalogEntryView) => {
			const subscriberProductIds = subscriberProducts.map(({ productId }) => productId);
			const { attributes } = singleCatalogEntryView;
			const reqProdIds = extractAttributeValuesAll({ attributes }, 'REQ_PROD').map((singleAttribute) =>
				getProductId(singleAttribute)
			);
			for (let i = 0; i < reqProdIds.length; i++) {
				if (subscriberProductIds.indexOf(reqProdIds[i]) !== -1) {
					return true;
				}
			}
			return false;
		},
		[subscriberProducts]
	);

	const activePlan: types.ecommerce.CatalogEntryView | undefined = useMemo(() => {
		const activeProductsIds = subscriberProducts.map(
			(singleSubscriberProduct) => singleSubscriberProduct.productId
		);
		return planCatalogEntryView.find((singleCatalogEntryView) =>
			activeProductsIds.includes(singleCatalogEntryView.partNumber)
		);
	}, [planCatalogEntryView, subscriberProducts]);

	const activeTopUps: Package[] = useMemo(() => {
		return packages.filter((singlePackage) => {
			const productType = singlePackage.commerceAttributes?.productType;
			return productType === 'GOBIZ_TOPUP';
		});
	}, [packages]);

	const filteredPlanCatalogEntryView: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return planCatalogEntryView.filter((singlePlanCatalogEntryView) => {
			return validateReqProd(singlePlanCatalogEntryView);
		});
	}, [validateReqProd, planCatalogEntryView]);

	const filteredTopUpCatalogEntryView: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return topUpCatalogEntryView.filter((singleTopUpCatalogEntryView) => {
			return validateReqProd(singleTopUpCatalogEntryView);
		});
	}, [validateReqProd, topUpCatalogEntryView]);

	const catalogEntryView: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		switch (dialogType) {
			case null:
				return [];
			case 'topUpDialog':
				return filteredTopUpCatalogEntryView;
			default:
				return filteredPlanCatalogEntryView;
		}
	}, [dialogType, filteredPlanCatalogEntryView, filteredTopUpCatalogEntryView]);

	const activeCatalogEntryView: types.ecommerce.CatalogEntryView | undefined = useMemo(() => {
		if (dialogType === 'changePlanDialog' || dialogType === 'planActivationDialog') {
			if (activePlan) {
				return activePlan;
			}
		}
		return undefined;
	}, [activePlan, dialogType]);

	const gobizDialogHeader: string = useMemo(() => {
		switch (dialogType) {
			case 'changePlanDialog':
				return translate('number.services.gobiz.change.plan');
			case 'planActivationDialog':
				return translate('number.services.gobiz.activation');
			case 'topUpDialog':
				return translate('number.services.gobiz.top.up');
			default:
				return '';
		}
	}, [dialogType]);

	const buttonText: string = useMemo(() => {
		switch (dialogType) {
			case 'changePlanDialog':
				return translate('change');
			case 'planActivationDialog':
				return translate('turnOn');
			case 'topUpDialog':
				return translate('number.services.top.up');
			default:
				return '';
		}
	}, [dialogType]);

	const getPlanName = (plan: types.ecommerce.CatalogEntryView | undefined) => {
		if (plan) {
			const price = plan.price.find((singlePrice) => singlePrice.usage === 'Offer');
			return `${plan.name} ${translate('number.services.price', price.value, price.currency)}`;
		}
		return '';
	};

	const changePlan = () => {
		showChangePlanDialog();
	};

	const gobizPackages = useMemo(() => {
		const isPending: boolean = pendingPlanPackages.length > 0 || pendingTopUpPackages.length > 0;
		const activeStatus: types.services.IStatus = {
			type: 'ACTIVE',
		};
		const pendingStatus: types.services.IStatus = {
			type: 'PENDING',
		};
		const pendingPlanPackagesIds = pendingPlanPackages.map(({ partNumber }) => getProductId(partNumber));
		const activePendingPackage =
			!!activePlan &&
			subscriberProducts.find(({ productId, status }) => {
				return status !== 'Active' && pendingPlanPackagesIds.indexOf(productId) >= 0;
			});
		return [
			{
				name: translate('number.services.gobiz'),
				description: translate('number.services.gobiz.terms'),
				id: 'GOBIZ',
				status:
					isPending || activePlan
						? undefined
						: {
								type: 'INACTIVE',
								text: translate('badge.inactive'),
						  },
				legacyProductId: '',
				place: '',
				buttons: [
					filteredTopUpCatalogEntryView.length > 0 && {
						text: translate('number.services.top.up'),
						color: 'primary-light',
						disabled: isPending,
						type: 'TOP_UP',
					},
					!activePlan &&
						filteredPlanCatalogEntryView.length > 0 && {
							text: translate('numbers.services.turn.on'),
							color: 'primary-light',
							disabled: isPending,
							type: 'TURN_ON',
						},
				].filter(Boolean),
			},
			...activeTopUps.map((singleActiveTopUp) => {
				const activePendingTopUp = subscriberProducts.find(({ productId, status }) => {
					return (
						status !== 'Active' &&
						(productId === getProductId(singleActiveTopUp.packageId) ||
							productId === getProductId(singleActiveTopUp.commerceUniqueIdToActivate))
					);
				});
				return {
					name: translate('number.services.gobiz'),
					description: goBizPackageDescription(singleActiveTopUp),
					id: singleActiveTopUp.packageId,
					status: activePendingTopUp
						? { ...pendingStatus, date: activePendingTopUp.startDate }
						: activeStatus,
					legacyProductId: '',
					place: '',
					buttons: [],
				};
			}),
			!!activePlan && {
				name: translate('number.services.gobiz'),
				description: getPlanName(activePlan),
				id: activePlan.uniqueID,
				legacyProductId: '',
				place: '',
				status: !!activePendingPackage
					? { ...pendingStatus, date: activePendingPackage.startDate }
					: { ...activeStatus, hidden: true },
				buttons: [
					{
						text: translate('services.number.gobiz.change.plan'),
						color: 'tertiary-light',
						action: changePlan,
						disabled: !!activePendingPackage,
						type: 'TURN_ON',
					},
					{
						text: translate('numbers.services.turn.off'),
						color: 'tertiary-light',
						disabled: !!activePendingPackage,
						type: 'TURN_OFF',
					},
				],
			},
		].filter(Boolean) as types.services.IPackage[];
		// eslint-disable-next-line
	}, [
		activePlan,
		activeTopUps,
		filteredTopUpCatalogEntryView,
		filteredPlanCatalogEntryView,
		pendingTopUpPackages,
		pendingPlanPackages,
		language,
	]);

	const turnOffGobizService = () => {
		if (activePlan) {
			// remove active plan by adding default one
			const ebuConfigProduct = extractAttributeValue(
				{ attributes: activePlan.attributes },
				CustomAttribute.EBU_CONFIG_PRODUCT
			);
			const orderItem: types.services.IOrderItem = {
				action: 'ADD',
				id: defaultRoamingPayG.id,
				place: defaultRoamingPayG.place,
				ebuConfigProduct,
			};
			updateOrderItems([orderItem], 'TURN_OFF', activePlan.name);
		}
	};

	const updateGobiz = (singlePackage: types.services.IPackage, type?: types.services.actionType) => {
		if (type === 'TURN_ON' && filteredPlanCatalogEntryView.length > 0 && singlePackage.id === 'GOBIZ') {
			if (roamingEnabled) {
				showPlanActivationDialog();
			} else {
				showRoamingDialog();
			}
		} else if (type === 'TOP_UP' && filteredTopUpCatalogEntryView.length > 0 && singlePackage.id === 'GOBIZ') {
			showTopUpDialog();
		} else {
			turnOffGobizService();
		}
	};

	useEffect(() => {
		setPendingStatus(pendingTopUpPackages.length > 0 || pendingPlanPackages.length > 0);
		// eslint-disable-next-line
	}, [pendingTopUpPackages, pendingPlanPackages]);

	useEffect(() => {
		setIsGoBizActive(gobizPackages.some((pckg) => pckg.status?.type === 'ACTIVE'));
	}, [gobizPackages]);

	return (
		<ConditionalRender
			show={loadingTopUps || loadingPlans}
			onTrue={() => <CustomCircularProgress text={translate('number.loading.packages')} loading />}
			onFalse={() => (
				<>
					<AccordionContent
						elements={gobizPackages}
						termsAndConditions={termsAndConditions}
						updateService={updateGobiz}
						confirmations={[
							{
								type: 'TURN_OFF',
								header: translate('number.services.gobiz.turn.off.confirmation.title'),
								dialogBody: translate(
									'number.services.gobiz.turn.off.confirmation',
									activePlan?.name || ''
								),
							},
						]}
					/>
					<ActivateGobizDialog
						isOpen={dialogType !== null}
						hideDialog={hideDialog}
						termsAndConditions={termsAndConditions}
						catalogEntryView={catalogEntryView}
						activeCatalogEntryView={activeCatalogEntryView}
						header={gobizDialogHeader}
						updateOrderItems={updateOrderItems}
						buttonText={buttonText}
						type={dialogType === 'topUpDialog' ? 'TOP_UP' : 'PLAN'}
						additionalText={dialogType === 'changePlanDialog' ? translate('number.services.downgrade') : ''}
					/>
					<RoamingInternationalActivationDialog
						isOpen={roamingDialogOpen}
						hideDialog={hideRoamingDialog}
						type="GOBIZ"
						updateOrderItems={updateOrderItems}
						isPending={roamingPending}
					/>
				</>
			)}
		/>
	);
};

export default GobizAccordionContentWrapper;
